import React, { useEffect, useState } from 'react';
import client from 'utils/client';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import './terminal.css';
import AppDataGrid from 'components/AppDataGrid/AppDataGrid';
import StatusPill, { ActivePill } from 'components/StatusPill/StatusPill';
import { Link } from 'react-router-dom';
import usePagination from 'hooks/usePagination';
import { useAuth } from 'contexts/AuthContext';

const formatMerchantName = (m) => {
  if (!m) return '';
  return `${m.first_name || ''} ${m.last_name || ''}`.trim();
};

function Terminals() {
  let navigate = useNavigate();
  const { isMerchant } = useAuth();

  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = React.useState(0);

  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);

  const {
    paginationData: { currentPage, pageSize },
    handlePageChange,
    handlePageSizeChange,
  } = usePagination();

  useEffect(() => {
    setLoading(true);

    client
      .get(`/terminals?page=${currentPage}&pageSize=${pageSize}`)
      .then((response) => {
        setTerminals(() => response.data.data);
        setTotalCount(response.data.pagination.totalCount);
      })
      .catch((error) => setError(error))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  const renderDetailsButton = (params) => (
    <button
      type='button'
      className='btn-secondary'
      onClick={() => navigate(`/terminals/${params.row.id}`)}>
      View
    </button>
  );

  const columns = [
    { field: 'terminal_id', headerName: 'Terminal ID', width: 130, cellClassName: 'cell-mono' },
    { field: 'serial_number', headerName: 'Serial Number', width: 150, cellClassName: 'cell-mono' },
    { field: 'agent_name', headerName: 'Agent', width: 140 },
    { field: 'agent_phone', headerName: 'Phone', width: 140, cellClassName: 'cell-mono' },
    { field: 'location', headerName: 'Location', width: 160 },
    {
      field: 'merchant',
      headerName: 'Merchant',
      width: 180,
      sortable: false,
      valueGetter: (p) => formatMerchantName(p.row.merchant) || `#${p.row.merchant_id}`,
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 110,
      renderCell: (p) => <ActivePill active={p.row.active} />,
    },
    {
      field: 'instant_settlement_enabled',
      headerName: 'Settlement',
      width: 120,
      renderCell: (p) => (
        <StatusPill
          label={p.row.instant_settlement_enabled ? 'Instant' : 'EOD'}
          variant={p.row.instant_settlement_enabled ? 'info' : 'neutral'}
        />
      ),
    },
    {
      field: 'settlement_process',
      headerName: 'Settlement Process',
      width: 160,
      renderCell: (p) => {
        const proc = p.row.settlement_process;
        if (!proc) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
        return (
          <StatusPill
            label={proc.charAt(0) + proc.slice(1).toLowerCase()}
            variant={proc === 'BULK' ? 'info' : 'neutral'}
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      valueGetter: (p) => dayjs(p.row.created_at).format('MMM DD, YYYY h:mma'),
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: renderDetailsButton,
    },
  ];

  return (
    <div className='terminal'>
      <div className='terminal-title-container'>
        <h1 className='terminal-title'>Terminals</h1>
        {!isMerchant && (
          <Link to='/new-terminal'>
            <button className='terminal-add-button'>Create</button>
          </Link>
        )}
      </div>
      <AppDataGrid
        className='terminals-list'
        pagination
        rows={terminals}
        columns={columns}
        page={currentPage - 1}
        pageSize={pageSize}
        rowCount={totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        paginationMode='server'
      />
    </div>
  );
}

export default Terminals;
